import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { IconButton, TextField, Autocomplete, Box, Button, Dialog, Typography } from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'

import { HighlightOff as HighlightOffIcon } from '@mui/icons-material'
import { toast } from 'react-toastify'
import { customReportingViewsService } from '../services/customReportingViewsService'
import { useAuthContext } from '../hooks/useAuthContext'
import FallbackCustomView from '../custom_views/FallbackCustomView'
import { formatUserDisplayName } from '../utility/userName'

const Views = () => {
  const { reporting_view_slug_or_id: reportingViewSlugOrId } = useParams()
  const { user } = useAuthContext()
  const [customData, setCustomData] = useState([])
  const [matchedViews, setMatchedViews] = useState(null)

  const companyId = 10
  const [owners, setOwners] = useState([])
  const [editors, setEditors] = useState([])
  const [viewers, setViewers] = useState([])
  const [isReadOnly, setIsReadOnly] = useState([])
  const [LoadedView, setLoadedView] = useState(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [viewConfig, setViewConfig] = useState({ hide_default_controller: false })
  const [hideAdminController, setHideAdminController] = useState(false)

  useEffect(() => {
    ;(async () => {
      const getCustomReportingViewData =
        await customReportingViewsService.getCustomReportingViewData(user, user.id)

      setCustomData(getCustomReportingViewData)

      // Try to find by slug first, then by ID (for backward compatibility)
      const matchedView = getCustomReportingViewData.find(
        (view) => view.slug == reportingViewSlugOrId || view.id == reportingViewSlugOrId,
      )

      setMatchedViews(matchedView)

      if (matchedView) {
        setOwners(matchedView.access?.owners)
        setEditors(matchedView.access?.editors)
        setViewers(matchedView.access?.viewers)
        setIsReadOnly(matchedView.is_readonly_access)
        setViewConfig(matchedView.config)
      }
      //  hide AdminControllerView for these folders
      const shouldHide = matchedView?.view_folder_name === 'DmsDashboardViews'

      setHideAdminController(shouldHide)
      const loadModules = async () => {
        try {
          const { default: MyModule } = await import(
            `./${matchedView.company_id}/${matchedView.view_folder_name}/index.js`
          )
          setLoadedView(() => MyModule) // Set the module as a component
          console.log('Module loaded successfully')
        } catch (error) {
          console.log('Error loading module:', error)
          setLoadedView(() => () => <FallbackCustomView message="Module not available." />)
        }
      }

      if (matchedView?.company_id && matchedView?.view_folder_name) {
        loadModules()
      }
    })()
  }, [user, companyId, reportingViewSlugOrId, isDialogOpen])

  return (
    <>
      {!viewConfig?.hide_default_controller ? (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div
            style={{
              fontWeight: 'bold',
              alignItems: 'center',
            }}
          >
            {customData.map((it) =>
              it.slug == reportingViewSlugOrId || it.id == reportingViewSlugOrId ? (
                <div key={it.id} style={{ fontSize: '18px', color: '#27104E' }}>
                  {it.name}
                </div>
              ) : null,
            )}
          </div>
          {!hideAdminController && (
            <AdminControllerView
              isDialogOpen={isDialogOpen}
              setIsDialogOpen={setIsDialogOpen}
              reportingViewId={matchedViews?.id}
              customData={customData}
              isReadOnly={isReadOnly}
              viewers={viewers}
              editors={editors}
              owners={owners}
              setViewers={setViewers}
              setEditors={setEditors}
              setOwners={setOwners}
            />
          )}
        </Box>
      ) : null}
      {/* Render ReportingCustomViews */}
      {(owners.includes(user.id) ||
        viewers.includes(user.id) ||
        editors.includes(user.id) ||
        isReadOnly) && (
        <>
          {LoadedView ? (
            <LoadedView
              renderController={() => (
                <AdminControllerView
                  isDialogOpen={isDialogOpen}
                  setIsDialogOpen={setIsDialogOpen}
                  reportingViewId={matchedViews?.id}
                  customData={customData}
                  isReadOnly={isReadOnly}
                  viewers={viewers}
                  editors={editors}
                  owners={owners}
                  setViewers={setViewers}
                  setEditors={setEditors}
                  setOwners={setOwners}
                />
              )}
              config={customData}
            />
          ) : (
            <div>Loading custom view...</div>
          )}
        </>
      )}
    </>
  )
}

const AdminControllerView = ({
  reportingViewId,
  isDialogOpen,
  setIsDialogOpen,
  customData,
  isReadOnly,
  viewers,
  setViewers,
  editors,
  setEditors,
  owners,
  setOwners,
}) => {
  const [userData, setUserData] = useState([])
  const { user } = useAuthContext()

  useEffect(() => {
    ;(async () => {
      const getCustomviewUserDetails = await customReportingViewsService.getCustomviewUserDetails(
        user,
        user.company_id,
      )

      setUserData(getCustomviewUserDetails)
    })()
  }, [])

  const mapUserToLabel = (userDatum) => formatUserDisplayName(userDatum)

  const sortedUserData = [...new Map(userData.map((user) => [user.id, user])).values()]
    .sort((a, b) => a.first_name?.localeCompare(b.first_name))
    .sort((a, b) => a.team_type?.localeCompare(b.team_type))

  const handleSettingsClick = (event) => {
    setIsDialogOpen(true)
  }

  const handleSettingsClose = () => {
    setIsDialogOpen(false)
  }

  const handleSaveUserDetails = async () => {
    await customReportingViewsService.saveCustomViewUSerDetails(user, {
      id: reportingViewId,
      owners: owners || [],
      editors: editors || [],
      viewers: viewers || [],
    })

    setIsDialogOpen(false)
  }

  return (
    <>
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {owners?.includes(user.id) && (
          <IconButton size="small" onClick={handleSettingsClick}>
            <SettingsIcon style={{ fontSize: '15px' }} />
          </IconButton>
        )}
      </Box>

      {/* Settings Menu */}
      <Dialog
        open={isDialogOpen}
        onClose={handleSettingsClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { maxWidth: 500, width: '100%', borderRadius: 2, p: 1 },
        }}
      >
        <Box sx={{ position: 'relative', p: 2 }}>
          {/* Close Button */}
          <IconButton onClick={handleSettingsClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
            <HighlightOffIcon />
          </IconButton>

          {/* Heading */}
          <Typography variant="h6" style={{ fontSize: '14px', fontWeight: 700 }} mb={2}>
            Access Settings
          </Typography>

          {/* Autocomplete for Owners */}
          <Autocomplete
            multiple
            disableClearable
            size="small"
            options={sortedUserData}
            groupBy={(user) => user.team_type}
            getOptionLabel={mapUserToLabel}
            value={sortedUserData.filter((user) => owners.includes(user.id))}
            onChange={(event, newValue) => {
              if (owners?.length === 1 && newValue?.length === 0) {
                toast.warning('At least one owner is required!')
                return
              }
              setOwners(newValue.map((us) => us.id))
            }}
            renderInput={(params) => <TextField {...params} label="Owners" size="small" />}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{
              mb: 2,
              '& .MuiAutocomplete-tag': {
                backgroundColor: 'rgb(242, 238, 255)',
                color: '#656d90',
                fontSize: '11px',
                lineHeight: '15px',
                fontWeight: '600',
              },
            }}
          />

          {/* Autocomplete for Editors */}
          <Autocomplete
            multiple
            disableClearable
            size="small"
            options={sortedUserData.filter((user) => !owners.includes(user.id))}
            groupBy={(user) => user.team_type}
            getOptionLabel={mapUserToLabel}
            value={sortedUserData.filter((user) => editors.includes(user.id))}
            onChange={(event, newValue) => {
              setEditors(newValue.map((user) => user.id))
            }}
            renderInput={(params) => <TextField {...params} label="Editors" size="small" />}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{ mb: 2 }}
          />

          {/* Autocomplete for Viewers */}
          {!isReadOnly && (
            <Autocomplete
              multiple
              disableClearable
              size="small"
              options={sortedUserData.filter(
                (user) => !owners.includes(user.id) && !editors.includes(user.id),
              )}
              groupBy={(user) => user.team_type}
              getOptionLabel={mapUserToLabel}
              value={sortedUserData.filter((user) => viewers.includes(user.id))}
              onChange={(event, newValue) => setViewers(newValue.map((user) => user.id))}
              renderInput={(params) => <TextField {...params} label="Viewers" size="small" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ mb: 2 }}
            />
          )}

          {/* Save Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              color="primary"
              variant="contained"
              size="small"
              onClick={handleSaveUserDetails}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  )
}

export default Views
